import { useRef, useEffect } from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import GoldMarquee from "@/components/Marquee";
import Calculator from "@/components/Calculator";
import Manifesto from "@/components/Manifesto";
import AboutUs from "@/components/AboutUs";
import WhyUs from "@/components/WhyUs";
import ObnovDom from "@/components/ObnovDom";
import Trust from "@/components/Trust";
import Brands from "@/components/Brands";
import Reviews from "@/components/Reviews";
import ProjectsMap from "@/components/ProjectsMap";
import Gallery from "@/components/Gallery";
import Cities from "@/components/Cities";
import KeyFacts from "@/components/KeyFacts";
import WorkPricing from "@/components/WorkPricing";
import HowItWorks from "@/components/HowItWorks";
import VisualizePromo from "@/components/VisualizePromo";
import GalleryTeaser from "@/components/GalleryTeaser";
import SdPromo from "@/components/SdPromo";
import StickyCTA from "@/components/StickyCTA";
import Footer from "@/components/Footer";
import JsonLd from "@/components/JsonLd";
import { scrollToId, SITE_URL, track, COMPANY, PHONE_TEL, SOCIALS } from "@/lib/api";

const SECTION_LABELS = {
  top: "Úvod (hero)",
  "ako-to-funguje": "Ako to funguje",
  kalkulacka: "Kalkulačka",
  vizualizacia: "Vizualizácia",
  proces: "Postup realizácie",
  "o-nas": "O nás",
  spolahlivost: "Spoľahlivosť",
  mapa: "Mapa realizácií",
  referencie: "Referencie",
  mesta: "Mestá",
};

export default function Home() {
  const scrollToCalc = () => scrollToId("#kalkulacka");

  useEffect(() => {
    const fired = new Set();
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const id = e.target.id;
          if (e.isIntersecting && id && SECTION_LABELS[id] && !fired.has(id)) {
            fired.add(id);
            track("view_section", SECTION_LABELS[id]);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    Object.keys(SECTION_LABELS).forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const orgLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        "name": "Zateplenia.eu",
        "legalName": COMPANY.name,
        "url": SITE_URL,
        "foundingDate": "2006",
        "logo": `${SITE_URL}/favicon.ico`,
        "image": `${SITE_URL}/og-image.jpg`,
        "description": "Zateplenie fasád rodinných domov, bytových domov a budov po celom Slovensku — online kalkulačka ceny, bezplatná AI vizualizácia fasády a sieť overených realizačných partnerov.",
        "telephone": PHONE_TEL,
        "email": COMPANY.email,
        "vatID": COMPANY.icdph,
        "taxID": COMPANY.ico,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": COMPANY.street,
          "addressLocality": "Bratislava",
          "postalCode": "851 01",
          "addressCountry": "SK",
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": PHONE_TEL,
          "contactType": "customer service",
          "email": COMPANY.email,
          "areaServed": "SK",
          "availableLanguage": "Slovak",
        },
        "areaServed": { "@type": "Country", "name": "Slovensko" },
        "knowsAbout": ["zateplenie fasád", "ETICS", "kontaktné zatepľovacie systémy", "fasádne omietky", "energetická úspora"],
        ...(SOCIALS.length ? { "sameAs": SOCIALS } : {}),
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        "url": SITE_URL,
        "name": "Zateplenia.eu",
        "inLanguage": "sk-SK",
        "publisher": { "@id": `${SITE_URL}/#organization` },
      },
      {
        "@type": "Service",
        "name": "Zateplenie fasády",
        "provider": { "@id": `${SITE_URL}/#organization` },
        "areaServed": { "@type": "Country", "name": "Slovensko" },
        "serviceType": "Zateplenie fasád rodinných a bytových domov",
      },
    ],
  };

  return (
    <div>
      <JsonLd id="ld-home" data={orgLd} />
      <Header />
      <Hero onCta={scrollToCalc} />
      <GoldMarquee />
      <HowItWorks />
      <Calculator />
      <ProjectsMap />
      <WhyUs />
      <VisualizePromo />
      <GalleryTeaser />
      <Manifesto />
      <AboutUs />
      <Trust />
      <ObnovDom />
      <Brands />
      <Reviews />
      <Gallery />
      <Cities />
      <WorkPricing />
      <KeyFacts />
      <SdPromo context="home" />
      <Footer />
      <StickyCTA context="home" />
    </div>
  );
}
